import React from 'react';

import { FormControl, IconButton, InputAdornment, MenuItem, Select, TextField } from '@mui/material';
import type { FormControlProps, SelectChangeEvent, SelectProps, TextFieldProps } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { NumberTextField } from './number_text_field';
import { i18n } from '../i18n/i18n';

export type SelectWithOptionProps = {
  textFieldMode?: 'text' | 'number';
  onChange?: (event: SelectChangeEvent, value: string | null) => void;
  onValueChange?: (value: string | null) => void;
  TextFieldProps?: TextFieldProps;
  TextFieldFormControlProps?: FormControlProps;
} & Omit<SelectProps, 'onChange'>;

export const SelectWithOption = React.forwardRef(
  (
    {
      textFieldMode = 'text',
      TextFieldProps,
      TextFieldFormControlProps,
      value,
      onChange,
      onValueChange,
      children,
      ...props
    }: SelectWithOptionProps,
    ref,
  ) => {
    const inputRef = React.useRef<unknown>(null);

    const CustomTextField = textFieldMode === 'number' ? NumberTextField : TextField;

    const [selection, setSelection] = React.useState<string | '_input' | null>((value as string | null) ?? null);
    const [valueInternal, setValueInternal] = React.useState(selection);

    const onChangeHandler = (
      event: SelectChangeEvent<unknown> | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
      newValue: string | null,
    ) => {
      const newEvent = { ...event, target: { ...event.target, value: newValue ?? '' } } as SelectChangeEvent;
      if (onChange) {
        onChange(newEvent, newValue);
      }
      if (onValueChange) {
        onValueChange(newValue);
      }
    };

    const onSelectionChangeHandler = (event: SelectChangeEvent<unknown>, _child: React.ReactNode) => {
      const selectedValue = event.target.value as string | null;
      setSelection(selectedValue);
      setValueInternal(null);
      if (selectedValue === '_input') {
        onChangeHandler(event, '');
        // @ts-expect-error ref any type
        setTimeout(() => inputRef.current?.focus(), 10);
      } else {
        onChangeHandler(event, selectedValue);
      }
    };

    const onTextFieldChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { value: inputValue } = event.target;
      setValueInternal(inputValue);
      onChangeHandler(event, inputValue);
    };

    return selection !== '_input' ? (
      <Select ref={ref} {...props} value={selection} onChange={onSelectionChangeHandler}>
        {children}
        <MenuItem value="_input">{i18n.t('general.other')}</MenuItem>
      </Select>
    ) : (
      <FormControl {...TextFieldFormControlProps}>
        <CustomTextField
          inputRef={inputRef}
          placeholder={i18n.t('general.other_please_specify')}
          {...TextFieldProps}
          value={selection === '_input' ? value ?? valueInternal ?? '' : ''}
          onChange={onTextFieldChange}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  edge="end"
                  onClick={(event: unknown) => onSelectionChangeHandler(event as never, '_input')}
                  onMouseDown={(e) => e.preventDefault()}
                  onMouseUp={(e) => e.preventDefault()}
                >
                  <CloseIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </FormControl>
    );
  },
);
